import React from 'react';
import { Box } from '@mui/material';
import { PageHeader, LoadingState, ErrorState, MetricCard, StatusChip } from './shared/components';
import { useMLData } from './shared/hooks';
import { mlApiClient, formatters } from './shared/utils';

/**
 * Inference Monitoring - Platform Admin
 * 
 * PLATFORM-LEVEL FEATURE:
 * - Real-time ML inference metrics across ALL tenants
 * - Model health monitoring
 * - Performance tracking (latency, throughput, cache efficiency)
 * - System capacity planning
 * 
 * VISION ALIGNMENT: Cybersecurity Autopilot
 * - Monitor autonomous AI decision engine performance
 * - Detect degradation before it impacts security operations
 * - Ensure <100ms latency for real-time alert scoring
 * - Track cache efficiency for cost optimization
 * 
 * RECOVERED FROM: MLInferencePipeline.tsx (1,009 lines)
 * MODERNIZED: Tailwind CSS, React hooks, real backend APIs
 * SIMPLIFIED: Focus on essential metrics (progressive complexity)
 */

interface InferenceMetrics {
  total_predictions: number;
  average_latency_ms: number;
  throughput_per_second: number;
  active_models: number;
  error_rate: number;
  last_updated: string;
}

interface PerformanceInsights {
  cache_hit_rate_percent: number;
  p95_latency_ms: number;
  uptime_seconds: number;
  throughput_per_minute: number;
  cache_enabled: boolean;
  redis_status: string;
}

interface PerformanceTargets {
  target_cache_hit_rate: number;
  target_p95_latency_ms: number;
  target_error_rate: number;
  cache_hit_achievement: boolean;
  latency_achievement: boolean;
  error_rate_achievement: boolean;
}

interface ModelHealth {
  model_id: string;
  model_name: string;
  deployment_status: string;
  health_status: 'healthy' | 'degraded' | 'unhealthy';
  last_prediction: string | null;
  prediction_count: number;
  error_count: number;
  average_latency_ms: number;
  cache_hit_rate: number;
  performance_grade: string;
}

interface InferenceData {
  metrics: InferenceMetrics;
  performance_insights: PerformanceInsights;
  performance_targets: PerformanceTargets;
}

export const InferenceMonitoring: React.FC = () => {
  // Fetch inference metrics - polling every 30 seconds
  const { data: inferenceData, loading, error, refresh } = useMLData<InferenceData>(
    () => mlApiClient.getInferenceMetrics() as Promise<InferenceData>,
    { pollingInterval: 30000 } // 30 second refresh
  );
  
  // Fetch model health - polling every 30 seconds
  const { data: healthData } = useMLData<{ model_health: ModelHealth[] }>(
    () => mlApiClient.getModelHealth() as Promise<{ model_health: ModelHealth[] }>,
    { pollingInterval: 30000 } // 30 second refresh
  );

  const metrics = inferenceData?.metrics;
  const insights = inferenceData?.performance_insights;
  const targets = inferenceData?.performance_targets;
  const modelHealth = healthData?.model_health || [];

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'unhealthy': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getHealthStatusBg = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500/10 border-green-500/20';
      case 'degraded': return 'bg-yellow-500/10 border-yellow-500/20';
      case 'unhealthy': return 'bg-red-500/10 border-red-500/20';
      default: return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (loading) return <LoadingState message="Loading inference metrics..." />;
  if (error) return <ErrorState error={error} onRetry={refresh} />;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Inference Monitoring"
        subtitle="Real-time ML inference performance across the platform"
        onRefresh={refresh}
        loading={loading}
      />

      {/* Key Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm mb-1">Total Predictions</div>
            <div className="text-2xl font-semibold text-white">
              {metrics.total_predictions.toLocaleString()}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm mb-1">Avg Latency</div>
            <div className="text-2xl font-semibold text-white">
              {metrics.average_latency_ms.toFixed(1)}ms
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm mb-1">Throughput</div>
            <div className="text-2xl font-semibold text-white">
              {metrics.throughput_per_second.toFixed(1)}/s
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm mb-1">Active Models</div>
            <div className="text-2xl font-semibold text-white">
              {metrics.active_models}
            </div>
          </div>
        </div>
      )}

      {/* Performance Insights */}
      {insights && targets && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Performance Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Cache Hit Rate */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Cache Hit Rate</span>
                <span className={`font-semibold ${targets.cache_hit_achievement ? 'text-green-400' : 'text-yellow-400'}`}>
                  {insights.cache_hit_rate_percent.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${targets.cache_hit_achievement ? 'bg-green-500' : 'bg-yellow-500'}`}
                  style={{ width: `${Math.min(insights.cache_hit_rate_percent, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Target: {targets.target_cache_hit_rate}%
              </div>
            </div>

            {/* P95 Latency */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">P95 Latency</span>
                <span className={`font-semibold ${targets.latency_achievement ? 'text-green-400' : 'text-yellow-400'}`}>
                  {insights.p95_latency_ms.toFixed(1)}ms
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${targets.latency_achievement ? 'bg-green-500' : 'bg-yellow-500'}`}
                  style={{ width: `${Math.min((insights.p95_latency_ms / targets.target_p95_latency_ms) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Target: &lt;{targets.target_p95_latency_ms}ms
              </div>
            </div>

            {/* Error Rate */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Error Rate</span>
                <span className={`font-semibold ${targets.error_rate_achievement ? 'text-green-400' : 'text-red-400'}`}>
                  {metrics?.error_rate.toFixed(2)}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${targets.error_rate_achievement ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min((metrics?.error_rate || 0) / targets.target_error_rate * 100, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Target: &lt;{targets.target_error_rate}%
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Uptime</div>
              <div className="text-white font-medium">{formatUptime(insights.uptime_seconds)}</div>
            </div>
            <div>
              <div className="text-gray-500">Throughput/min</div>
              <div className="text-white font-medium">{insights.throughput_per_minute.toFixed(0)}</div>
            </div>
            <div>
              <div className="text-gray-500">Cache Status</div>
              <div className={`font-medium ${insights.cache_enabled ? 'text-green-400' : 'text-gray-400'}`}>
                {insights.redis_status}
              </div>
            </div>
            <div>
              <div className="text-gray-500">System Health</div>
              <div className="text-green-400 font-medium">Operational</div>
            </div>
          </div>
        </div>
      )}

      {/* Model Health Table */}
      {modelHealth.length > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Model Health Status</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Predictions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Errors
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Avg Latency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cache Hit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Grade
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {modelHealth.map((model) => (
                  <tr key={model.model_id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{model.model_name}</div>
                      <div className="text-xs text-gray-500">{model.deployment_status}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getHealthStatusBg(model.health_status)} ${getHealthStatusColor(model.health_status)}`}>
                        {model.health_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {model.prediction_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={model.error_count > 0 ? 'text-red-400' : 'text-gray-400'}>
                        {model.error_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {model.average_latency_ms.toFixed(1)}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {(model.cache_hit_rate * 100).toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${
                        model.performance_grade === 'A' ? 'text-green-400' :
                        model.performance_grade === 'B' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {model.performance_grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {modelHealth.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
          <p className="text-gray-400">No models currently deployed</p>
        </div>
      )}
    </Box>
  );
};

export default InferenceMonitoring;
